import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, FileText, TriangleAlert } from 'lucide-react';
import { storedFileSchema, type StoredFileDto } from '@eombor/contracts';
import { Button, Skeleton, cn } from '@eombor/ui';
import { apiClient } from '../../features/auth/model/auth.store.js';
import { openBlob } from '../api/download.js';
import { t } from '../i18n/index.js';
import { AuthImage } from './AuthImage.js';

/**
 * BIRIKTIRILGAN FAYL — nomi bilan, ochiladigan holda.
 *
 * Ilgari bu yerda oddiy `<a href="/api/v1/files/<id>">` turardi va u
 * ISHLAMASDI: brauzer yangi varaqqa `Authorization` sarlavhasini
 * yubormaydi va odam akt o'rniga `{"code":"UNAUTHORIZED"}` matnini
 * ko'rardi. Tokenni manzilga yozish yechim emas — u brauzer tarixida,
 * proksi jurnalida va yelkadan qaragan ko'zda qolib ketadi.
 *
 * Shuning uchun baytlar `apiBlob` bilan olinadi (`AuthImage` dagi ayni
 * yo'l), fayl nomi esa `GET /files/:id/meta` dan: nomsiz biriktirma
 * "fayl" degan tugma bo'lib qolardi va odam uni ochmasdan nima ekanini
 * bilmasdi.
 *
 * Rasm bo'lsa ESKIZ ham chiziladi. Akt ko'pincha qog'ozning surati va
 * uni ochmasdan turib ko'rish — buxgalterning bir bosishini tejaydi.
 */

const IMAGE_PREFIX = 'image/';

const sizeText = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

export const fileMetaKey = (fileId: string) => ['files', 'meta', fileId] as const;

export interface FileAttachmentProps {
  fileId: string;
  /** Biriktirmaning ROLI — «Akt fayli», «Hisob-faktura». */
  label: string;
  className?: string;
}

export const FileAttachment = ({ fileId, label, className }: FileAttachmentProps) => {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const meta = useQuery<StoredFileDto>({
    queryKey: fileMetaKey(fileId),
    queryFn: () => apiClient.apiFetch(`/files/${fileId}/meta`, { schema: storedFileSchema }),
    retry: false,
    // Fayl O'ZGARMAS: yuklangach nomi ham, hajmi ham hech qachon
    // yangilanmaydi — qayta so'rashning ma'nosi yo'q.
    staleTime: Infinity,
  });

  const open = (): void => {
    setBusy(true);
    setFailed(false);
    void apiClient
      .apiBlob(`/files/${fileId}`)
      .then((blob) => openBlob(blob, meta.data?.originalName ?? `${fileId}.bin`))
      .catch(() => setFailed(true))
      .finally(() => setBusy(false));
  };

  if (meta.isPending) return <Skeleton className={cn('h-12 w-full', className)} />;

  const isImage = meta.data?.mimeType.startsWith(IMAGE_PREFIX) === true;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <span className="text-sm font-medium text-text">{label}</span>

      {isImage ? (
        <AuthImage
          src={`files/${fileId}`}
          alt={label}
          className={cn(
            'max-h-48 w-full rounded-[var(--radius-sm)] border border-border object-contain',
            'bg-surface-2',
          )}
          skeletonClassName="h-48"
        />
      ) : null}

      <div
        className={cn(
          'flex flex-wrap items-center gap-3 rounded-[var(--radius-md)] border border-border',
          'bg-surface-2 px-3 py-2',
        )}
      >
        <span aria-hidden className="shrink-0 text-text-faint">
          <FileText size={16} />
        </span>

        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm text-text">
            {/* Meta kelmasa ham tugma ISHLAYDI: baytlar boshqa manzildan
                olinadi va nomi faqat saqlashda kerak bo'ladi. */}
            {meta.data?.originalName ?? t('file.unnamed')}
          </span>
          {meta.data === undefined ? null : (
            <span className="tabular text-xs text-text-faint">
              {sizeText(meta.data.sizeBytes)}
            </span>
          )}
        </span>

        <Button variant="ghost" size="sm" loading={busy} onClick={open}>
          <Download size={14} aria-hidden />
          {t('file.open')}
        </Button>
      </div>

      {failed ? (
        <p role="alert" className="flex items-center gap-1.5 text-xs text-danger">
          <TriangleAlert size={12} aria-hidden />
          {t('file.openFailed')}
        </p>
      ) : null}
    </div>
  );
};
